import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from 'components/Toast';
import styles from "./employees.module.scss";
import { cn } from "utils";
import Input from "components/Input";
import Checkbox from "components/Checkbox";
import Select from "components/Select";
import Radio from "components/Radio";
import Button from "components/Button";
import AddressAutocomplete from "components/AddressAutocomplete";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { EmployeeInputs, getEmployeeSchema } from "./const";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getWorkplaces } from "services/workplaces";
import { getDepartments } from "services/departments";
import { createEmployee, updateEmployee, getEmployee, getEmployeeBankAccounts, createEmployeeBankAccount, updateEmployeeBankAccount, deleteEmployeeBankAccount, getEmployeeNetPaySplits, createEmployeeNetPaySplit, inviteEmployee }  from "services/employees";
import { getPaySchedules } from "services/paySchedules";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import axiosInstance from "../../axios/axiosInstance";

const US_TIME_ZONES = [
    { label: "Eastern Time (ET) - New York, Miami", value: "America/New_York" },
    { label: "Central Time (CT) - Chicago, Dallas", value: "America/Chicago" },
    { label: "Mountain Time (MT) - Denver, Salt Lake City", value: "America/Denver" },
    { label: "Mountain Time - Arizona (no DST)", value: "America/Phoenix" },
    { label: "Pacific Time (PT) - Los Angeles, Seattle", value: "America/Los_Angeles" },
    { label: "Alaska Time (AKT)", value: "America/Anchorage" },
    { label: "Hawaii Time (HT) - Honolulu", value: "Pacific/Honolulu" },
];

const US_STATES = [
    { label: "Alabama", value: "AL" }, { label: "Alaska", value: "AK" },
    { label: "Arizona", value: "AZ" }, { label: "Arkansas", value: "AR" },
    { label: "California", value: "CA" }, { label: "Colorado", value: "CO" },
    { label: "Connecticut", value: "CT" }, { label: "Delaware", value: "DE" },
    { label: "Florida", value: "FL" }, { label: "Georgia", value: "GA" },
    { label: "Hawaii", value: "HI" }, { label: "Idaho", value: "ID" },
    { label: "Illinois", value: "IL" }, { label: "Indiana", value: "IN" },
    { label: "Iowa", value: "IA" }, { label: "Kansas", value: "KS" },
    { label: "Kentucky", value: "KY" }, { label: "Louisiana", value: "LA" },
    { label: "Maine", value: "ME" }, { label: "Maryland", value: "MD" },
    { label: "Massachusetts", value: "MA" }, { label: "Michigan", value: "MI" },
    { label: "Minnesota", value: "MN" }, { label: "Mississippi", value: "MS" },
    { label: "Missouri", value: "MO" }, { label: "Montana", value: "MT" },
    { label: "Nebraska", value: "NE" }, { label: "Nevada", value: "NV" },
    { label: "New Hampshire", value: "NH" }, { label: "New Jersey", value: "NJ" },
    { label: "New Mexico", value: "NM" }, { label: "New York", value: "NY" },
    { label: "North Carolina", value: "NC" }, { label: "North Dakota", value: "ND" },
    { label: "Ohio", value: "OH" }, { label: "Oklahoma", value: "OK" },
    { label: "Oregon", value: "OR" }, { label: "Pennsylvania", value: "PA" },
    { label: "Rhode Island", value: "RI" }, { label: "South Carolina", value: "SC" },
    { label: "South Dakota", value: "SD" }, { label: "Tennessee", value: "TN" },
    { label: "Texas", value: "TX" }, { label: "Utah", value: "UT" },
    { label: "Vermont", value: "VT" }, { label: "Virginia", value: "VA" },
    { label: "Washington", value: "WA" }, { label: "West Virginia", value: "WV" },
    { label: "Wisconsin", value: "WI" }, { label: "Wyoming", value: "WY" },
    { label: "District of Columbia", value: "DC" },
];

const emptyBankAccount = () => ({ accountType: "", accountNumber: "", routingNumber: "" });
const maskAccount = (num) => num ? "****" + String(num).slice(-4) : "****";

const AddEditEmployee = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const [searchParams] = useSearchParams();
    const employeeId = searchParams.get("id");
    const isEditMode = !!employeeId;
    const highlightSetup = searchParams.get("highlight") === "setup";
    // Data pre-filled from the Pending Onboardings page (avoids a potentially incomplete single-employee API fetch)
    const prefillData = location.state?.employeeData ?? null;
    const fromOnboarding = location.state?.fromOnboarding === true;
    // Use the employee's own companyId when available — self-onboarded employees may be stored
    // under a different companyId than the one currently in localStorage.
    const effectiveCid = prefillData?.companyId || undefined;

    const [paymentMethod, setPaymentMethod] = useState("check");
    const [bankAccounts, setBankAccounts] = useState([emptyBankAccount()]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [sendInvite, setSendInvite] = useState(true);
    const [inviteResult, setInviteResult] = useState(null);
    const [splitMessage, setSplitMessage] = useState(null);
    const [editingBankAccountId, setEditingBankAccountId] = useState(null);
    const [editBankAccountData, setEditBankAccountData] = useState({});
    const [confirmRemoveId, setConfirmRemoveId] = useState(null);
    const [showSwitchToCheckConfirm, setShowSwitchToCheckConfirm] = useState(false);
    const [showAddBankForm, setShowAddBankForm] = useState(false);
    const [newBankAccount, setNewBankAccount] = useState(emptyBankAccount());
    const [splits, setSplits] = useState([]);
    const [addModeSplits, setAddModeSplits] = useState([]);

    // Skip the single-employee GET when prefillData is already available — the production API
    // may not have this endpoint, and the form is populated from prefillData instead.
    const { data: existingEmployee, isLoading: isLoadingEmployee } = useQuery({
        queryKey: ["employee", employeeId, effectiveCid],
        queryFn: () => getEmployee(employeeId, effectiveCid),
        enabled: isEditMode && !prefillData,
        retry: false,
    });

    const { data: bankAccountsRaw, isLoading: loadingBankAccounts, refetch: refetchBankAccounts } = useQuery({
        queryKey: ["employee-bank-accounts", employeeId, effectiveCid],
        queryFn: () => getEmployeeBankAccounts(employeeId, effectiveCid),
        enabled: isEditMode,
        retry: false,
    });

    const { data: workplacesRaw } = useQuery({ queryKey: ["workplaces"], queryFn: getWorkplaces });
    const workplacesList = Array.isArray(workplacesRaw) ? workplacesRaw : (workplacesRaw?.data ?? workplacesRaw?.items ?? []);
    const workplaceOptions = workplacesList.map(w => ({ label: w.name || w.city || w.id, value: w.id }));

    const { data: departmentsRaw } = useQuery({ queryKey: ["departments"], queryFn: getDepartments });
    const departmentOptions = (Array.isArray(departmentsRaw) ? departmentsRaw : (departmentsRaw?.data ?? departmentsRaw?.items ?? [])).map(d => ({ label: d.name, value: d.id }));

    const { data: paySchedulesRaw } = useQuery({ queryKey: ["pay-schedules"], queryFn: getPaySchedules });
    const payScheduleOptions = useMemo(() => {
        const list = Array.isArray(paySchedulesRaw) ? paySchedulesRaw : (paySchedulesRaw?.data ?? []);
        return [
            { label: "— Company default / unassigned —", value: "" },
            ...list.filter(s => s.active !== false).map(s => ({
                label: s.name || s.frequency || s.id,
                value: s.id,
            })),
        ];
    }, [paySchedulesRaw]);

    const companyId = localStorage.getItem("companyId");
    const lockedLocationId = localStorage.getItem("locationId") || "";
    const { data: locationsRaw } = useQuery({
        queryKey: ["company-locations", companyId],
        queryFn: () => axiosInstance.get(`/companies/${companyId}/locations`).then(r => r.data),
        enabled: !!companyId,
        retry: false,
    });
    const activeLocations = useMemo(
        () => (Array.isArray(locationsRaw) ? locationsRaw : []).filter(l => l.isActive !== false),
        [locationsRaw]
    );
    const requireLocation = activeLocations.length > 0;
    const requireLocationRef = useRef(false);
    requireLocationRef.current = requireLocation;
    const locationOptions = useMemo(() => {
        const opts = activeLocations.map(l => ({ label: l.name, value: l.id }));
        if (!requireLocation) return [{ label: "— No specific location —", value: "" }, ...opts];
        return [{ label: "Select branch / location…", value: "" }, ...opts];
    }, [activeLocations, requireLocation]);
    const locationAwareResolver = useMemo(
        () => async (values, context, options) =>
            yupResolver(getEmployeeSchema(requireLocationRef.current))(values, context, options),
        []
    );

    const existingBankAccounts = Array.isArray(bankAccountsRaw) ? bankAccountsRaw :
        (bankAccountsRaw?.data ?? bankAccountsRaw?.items ?? []);

    const { data: splitsRaw, refetch: refetchSplits } = useQuery({
        queryKey: ["employee-net-pay-splits", employeeId, effectiveCid],
        queryFn: () => getEmployeeNetPaySplits(employeeId, effectiveCid),
        enabled: isEditMode,
        retry: false,
    });
    const existingSplits = Array.isArray(splitsRaw) ? splitsRaw : (splitsRaw?.data ?? splitsRaw?.items ?? []);


    const hasDirectDeposit = existingBankAccounts.length > 0;

    useEffect(() => {
        // If bank accounts exist, this employee is on Direct Deposit
        if (existingBankAccounts.length > 0) {
            setPaymentMethod("direct_deposit");
        }
        if (existingSplits && existingSplits.length > 0) {
            setSplits(existingSplits.map(s => ({
                bankAccountId: s.bankAccountId || "",
                splitType: s.percent > 0 ? "percentage" : s.amount > 0 ? "flat" : "remaining_balance",
                value: s.percent > 0 ? s.percent : s.amount > 0 ? s.amount : null,
            })));
        } else if (existingBankAccounts.length > 0) {
            if (existingBankAccounts.length === 1) {
                setSplits([{ bankAccountId: existingBankAccounts[0].id, splitType: "remaining_balance", value: null }]);
            } else {
                const pct = Math.floor(100 / existingBankAccounts.length);
                setSplits(existingBankAccounts.map((b, i) => ({
                    bankAccountId: b.id,
                    splitType: i < existingBankAccounts.length - 1 ? "percentage" : "remaining_balance",
                    value: i < existingBankAccounts.length - 1 ? pct : null,
                })));
            }
        }
    }, [existingBankAccounts.length, existingSplits.length]);

    const { mutate: mutateUpdate, isPending: isUpdating, isError: isUpdateError, error: updateError } = useMutation({
        mutationFn: updateEmployee,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["employees"] });
            queryClient.invalidateQueries({ queryKey: ["employee", employeeId] });
            const isPendingOnboarding = fromOnboarding ||
                existingEmployee?.onboardStatus === "pending_review" ||
                existingEmployee?.onboardingStatus === "pending_review" ||
                prefillData?.onboardStatus === "pending_review" ||
                prefillData?.onboardingStatus === "pending_review";
            navigate(isPendingOnboarding ? "/onboarding" : "/employees");
        },
        onError: (err) => {
            const data = err?.response?.data;
            console.error("Update employee error:", data);
            if (typeof data === "string" && data.includes("<!DOCTYPE")) {
                setSubmitError("Server error (500). See Network tab → failed PATCH → Response for details.");
            } else if (data?.message) {
                setSubmitError(data.message);
            }
        },
    });

    const isPending = isUpdating;
    const isError = isUpdateError;
    const error = updateError;

    const {
        register,
        handleSubmit,
        reset,
        watch,
        getValues,
        setValue,
        trigger,
        formState: { errors },
    } = useForm({
        resolver: locationAwareResolver,
        mode: 'onChange',
        defaultValues: {
            [EmployeeInputs.WORK_FROM_HOME]: "yes",
            [EmployeeInputs.ACTIVE_STATUS]: "true",
            [EmployeeInputs.LOCATION_ID]: lockedLocationId || "",
        }
    });

    useEffect(() => {
        if (lockedLocationId) {
            setValue(EmployeeInputs.LOCATION_ID, lockedLocationId);
        }
    }, [lockedLocationId, setValue]);

    useEffect(() => {
        // Merge both data sources: prefill (from list) provides a base; the single-employee
        // API response (when valid) provides the full record including emergency contact, I-9, etc.
        const apiOk = existingEmployee && !existingEmployee.error && existingEmployee.id;
        const emp = apiOk
            ? (prefillData ? { ...prefillData, ...existingEmployee } : existingEmployee)
            : prefillData;
        if (!emp) return;
        reset({
            [EmployeeInputs.FIRSTNAME]: emp.firstName || "",
            [EmployeeInputs.MIDDLENAME]: emp.middleName || "",
            [EmployeeInputs.LASTNAME]: emp.lastName || "",
            [EmployeeInputs.SOCIAL_SECURITY_NUMBER]: emp.ssn || "",
            [EmployeeInputs.BIRTH_DATE]: emp.dateOfBirth ? emp.dateOfBirth.substring(0, 10) : "",
            [EmployeeInputs.MARITAL_STATUS]: emp.maritalStatus || "",
            [EmployeeInputs.ADDRESS]: emp.addressLine1 || "",
            [EmployeeInputs.CITY]: emp.city || "",
            [EmployeeInputs.STATE]: emp.state || "",
            [EmployeeInputs.ZIP_CODE]: emp.zip || "",
            [EmployeeInputs.EMAIL]: emp.email || "",
            [EmployeeInputs.HOME_PHONE]: emp.homePhone || emp.phone || "",
            [EmployeeInputs.CELL_PHONE]: emp.cellPhone || emp.phone || "",
            [EmployeeInputs.WORK_FROM_HOME]: emp.workFromHome || "yes",
            [EmployeeInputs.WORK_LOCATION]: emp.workplaceId || emp.workLocation || "",
            [EmployeeInputs.LOCATION_ID]: emp.locationId || "",
            [EmployeeInputs.PAYMENT_SCHEDULE_ID]: emp.paymentScheduleId || "",
            [EmployeeInputs.WORK_TIME_ZONE]: emp.workTimeZone || "",
            [EmployeeInputs.IS_REG_EMAIL]: emp.sendPortalEmail ?? false,
            [EmployeeInputs.ACTIVE_STATUS]: emp.active !== false ? "true" : "false",
            [EmployeeInputs.WAGE]: emp.payRate != null && parseFloat(emp.payRate) > 0 ? String(emp.payRate) : "",
            [EmployeeInputs.WAGE_PERIOD]: emp.payType || "",
            [EmployeeInputs.PAY_TYPE]: emp.payType || "",
            [EmployeeInputs.FULL_TIME_HOURS]: emp.fullTimeHours != null && parseFloat(emp.fullTimeHours) > 0 ? String(emp.fullTimeHours) : "",
            [EmployeeInputs.OVERTIME_EXEMPT_STATUS]: emp.flsaOvertimeExemptionStatus || "",
            [EmployeeInputs.SUPPLEMENT_TAX_METHOD]: emp.supplementTaxMethod || "",
            [EmployeeInputs.PREFERRED_NAME]: emp.preferredName || "",
            [EmployeeInputs.JOB_TITLE]: emp.jobTitle || "",
            [EmployeeInputs.DEPARTMENT]: emp.departmentId || emp.department || "",
            [EmployeeInputs.EMPLOYMENT_TYPE]: emp.employmentType || "",
            [EmployeeInputs.FEDERAL_FILING_STATUS]: emp.federalFilingStatus || "S",
            [EmployeeInputs.FEDERAL_EXEMPT]: emp.federalExempt ?? false,
            [EmployeeInputs.FEDERAL_EXTRA_WITHHOLDING]: emp.federalExtraWithholding != null ? String(emp.federalExtraWithholding) : "0",
            [EmployeeInputs.MULTIPLE_JOBS]: emp.multipleJobs || "",
            [EmployeeInputs.DEPENDENTS_AMOUNT]: emp.dependentsAmount != null ? String(emp.dependentsAmount) : "",
            [EmployeeInputs.OTHER_INCOME]: emp.otherIncome != null ? String(emp.otherIncome) : "",
            [EmployeeInputs.DEDUCTIONS_AMOUNT]: emp.deductionsAmount != null ? String(emp.deductionsAmount) : "",
            [EmployeeInputs.STATE_FILING_STATUS]: emp.stateFilingStatus || "",
            [EmployeeInputs.STATE_ALLOWANCES]: emp.stateAllowances ?? "",
            [EmployeeInputs.RESIDENT_STATE]: emp.residentState || "",
            [EmployeeInputs.WORK_STATE]: emp.workState || "",
            [EmployeeInputs.START_DATE]: emp.startDate ? emp.startDate.substring(0, 10) : "",
            [EmployeeInputs.TERMINATION_DATE]: emp.terminationDate ? emp.terminationDate.substring(0, 10) : "",
            [EmployeeInputs.EMERGENCY_FIRST_NAME]: emp.emergencyContactFirstName || "",
            [EmployeeInputs.EMERGENCY_LAST_NAME]: emp.emergencyContactLastName || "",
            [EmployeeInputs.EMERGENCY_RELATIONSHIP]: emp.emergencyContactRelationship || "",
            [EmployeeInputs.EMERGENCY_PHONE]: emp.emergencyContactPhone || "",
            [EmployeeInputs.EMERGENCY_ALT_PHONE]: emp.emergencyContactAltPhone || "",
            [EmployeeInputs.EMERGENCY_EMAIL]: emp.emergencyContactEmail || "",
            [EmployeeInputs.US_CITIZEN]: emp.usCitizen || "",
            [EmployeeInputs.AUTHORIZED_TO_WORK]: emp.authorizedToWork || "",
            [EmployeeInputs.WORK_AUTH_TYPE]: emp.workAuthType || "",
            [EmployeeInputs.WORK_AUTH_EXPIRY]: emp.workAuthExpiry ? emp.workAuthExpiry.substring(0, 10) : "",
            [EmployeeInputs.REHIRE]: emp.rehire || "",
            [EmployeeInputs.I9_SECTION1]: emp.i9Section1Completed || "",
            [EmployeeInputs.I9_DATE]: emp.i9Date ? emp.i9Date.substring(0, 10) : "",
            [EmployeeInputs.PRIMARY_WORK_STATE]: emp.primaryWorkState || "",
            [EmployeeInputs.REMOTE_WORK_STATE]: emp.remoteWorkState || "",
            [EmployeeInputs.WORKERS_COMP_CODE]: emp.workersCompCode || "",
            [EmployeeInputs.LOCAL_TAX_AREA]: emp.localTaxArea || "",
        });
        // Restore payment method state from saved employee data
        if (emp.defaultPaymentMethod) {
            setPaymentMethod(emp.defaultPaymentMethod);
        }
        // Reset the address state tracker so the next change after load triggers the prompt
        addressStateReady.current = false;
    }, [existingEmployee, prefillData, reset]);

    const buildPayload = useCallback((values) => {
        const startDate = values[EmployeeInputs.START_DATE];
        const terminationDate = values[EmployeeInputs.TERMINATION_DATE];
        const selectedDeptId = values[EmployeeInputs.DEPARTMENT] || null;
        const selectedWpId   = values[EmployeeInputs.WORK_LOCATION] || null;
        const deptName = departmentOptions.find(d => d.value === selectedDeptId)?.label || null;
        const wpName   = workplaceOptions.find(w => w.value === selectedWpId)?.label || null;
        return {
            firstName: values[EmployeeInputs.FIRSTNAME],
            middleName: values[EmployeeInputs.MIDDLENAME],
            lastName: values[EmployeeInputs.LASTNAME],
            preferredName: values[EmployeeInputs.PREFERRED_NAME] || null,
            ssn: values[EmployeeInputs.SOCIAL_SECURITY_NUMBER],
            email: values[EmployeeInputs.EMAIL],
            homePhone: values[EmployeeInputs.HOME_PHONE] || null,
            cellPhone: values[EmployeeInputs.CELL_PHONE] || null,
            dateOfBirth: values[EmployeeInputs.BIRTH_DATE] || null,
            maritalStatus: values[EmployeeInputs.MARITAL_STATUS] || null,
            jobTitle: values[EmployeeInputs.JOB_TITLE] || null,
            departmentId: selectedDeptId,
            department: deptName,
            employmentType: values[EmployeeInputs.EMPLOYMENT_TYPE] || null,
            residence: {
                street1: values[EmployeeInputs.ADDRESS],
                city: values[EmployeeInputs.CITY],
                state: values[EmployeeInputs.STATE],
                zipcode: values[EmployeeInputs.ZIP_CODE],
            },
            wage: {
                rate: parseFloat(values[EmployeeInputs.WAGE]) || 0,
                period: values[EmployeeInputs.PAY_TYPE] || "salary",
                fullTimeHours: parseFloat(values[EmployeeInputs.FULL_TIME_HOURS]) || 40,
                flsaOvertimeExemptionStatus: values[EmployeeInputs.OVERTIME_EXEMPT_STATUS] || null,
                supplementalTaxMethod: values[EmployeeInputs.SUPPLEMENT_TAX_METHOD] || null,
            },
            workFromHome: values[EmployeeInputs.WORK_FROM_HOME] || null,
            workplaceId: selectedWpId,
            workLocation: wpName,
            locationId: values[EmployeeInputs.LOCATION_ID] || null,
            paymentScheduleId: values[EmployeeInputs.PAYMENT_SCHEDULE_ID] || null,
            workTimeZone: values[EmployeeInputs.WORK_TIME_ZONE] || null,
            federalFilingStatus: values[EmployeeInputs.FEDERAL_FILING_STATUS] || "S",
            federalExempt: values[EmployeeInputs.FEDERAL_EXEMPT] === true || values[EmployeeInputs.FEDERAL_EXEMPT] === "true",
            federalExtraWithholding: parseFloat(values[EmployeeInputs.FEDERAL_EXTRA_WITHHOLDING]) || 0,
            multipleJobs: values[EmployeeInputs.MULTIPLE_JOBS] || null,
            dependentsAmount: parseFloat(values[EmployeeInputs.DEPENDENTS_AMOUNT]) || 0,
            otherIncome: parseFloat(values[EmployeeInputs.OTHER_INCOME]) || 0,
            deductionsAmount: parseFloat(values[EmployeeInputs.DEDUCTIONS_AMOUNT]) || 0,
            stateFilingStatus: values[EmployeeInputs.STATE_FILING_STATUS] || null,
            stateAllowances: values[EmployeeInputs.STATE_ALLOWANCES] !== "" ? parseInt(values[EmployeeInputs.STATE_ALLOWANCES], 10) : null,
            residentState: values[EmployeeInputs.RESIDENT_STATE] || null,
            workState: values[EmployeeInputs.WORK_STATE] || null,
            active: values[EmployeeInputs.ACTIVE_STATUS] !== "false",
            defaultPaymentMethod: paymentMethod,
            sendPortalEmail: values[EmployeeInputs.IS_REG_EMAIL] === true || values[EmployeeInputs.IS_REG_EMAIL] === "true",
            startDate: startDate ? startDate + "T00:00:00" : null,
            terminationDate: terminationDate ? terminationDate + "T00:00:00" : null,
            emergencyContactFirstName: values[EmployeeInputs.EMERGENCY_FIRST_NAME] || null,
            emergencyContactLastName: values[EmployeeInputs.EMERGENCY_LAST_NAME] || null,
            emergencyContactRelationship: values[EmployeeInputs.EMERGENCY_RELATIONSHIP] || null,
            emergencyContactPhone: values[EmployeeInputs.EMERGENCY_PHONE] || null,
            emergencyContactAltPhone: values[EmployeeInputs.EMERGENCY_ALT_PHONE] || null,
            emergencyContactEmail: values[EmployeeInputs.EMERGENCY_EMAIL] || null,
            usCitizen: values[EmployeeInputs.US_CITIZEN] || null,
            authorizedToWork: values[EmployeeInputs.AUTHORIZED_TO_WORK] || null,
            workAuthType: values[EmployeeInputs.WORK_AUTH_TYPE] || null,
            workAuthExpiry: values[EmployeeInputs.WORK_AUTH_EXPIRY] || null,
            rehire: values[EmployeeInputs.REHIRE] || null,
            i9Section1Completed: values[EmployeeInputs.I9_SECTION1] || null,
            i9Date: values[EmployeeInputs.I9_DATE] || null,
            primaryWorkState: values[EmployeeInputs.PRIMARY_WORK_STATE] || null,
            remoteWorkState: values[EmployeeInputs.REMOTE_WORK_STATE] || null,
            workersCompCode: values[EmployeeInputs.WORKERS_COMP_CODE] || null,
            localTaxArea: values[EmployeeInputs.LOCAL_TAX_AREA] || null,
        };
    }, [paymentMethod, departmentOptions, workplaceOptions]);

    const onSubmit = useCallback(async (values) => {
        if (requireLocationRef.current && !String(values[EmployeeInputs.LOCATION_ID] || '').trim()) {
            setSubmitError("Branch / Location is required.");
            toast.error("Branch / Location is required.");
            return;
        }
        const payload = buildPayload(values);
        setSubmitError(null);
        if (isEditMode) {
            mutateUpdate({ employeeId, _companyId: effectiveCid, ...payload });
        } else {
            if (paymentMethod === "direct_deposit" && bankAccounts.length >= 2 && addModeSplits.length >= 2) {
                const remCount = addModeSplits.filter(s => s.splitType === "remaining_balance").length;
                const totalPct = addModeSplits.filter(s => s.splitType === "percentage").reduce((sum, s) => sum + (parseFloat(s.value) || 0), 0);
                if (remCount > 1) {
                    setSubmitError("Only one split can be Remaining Balance.");
                    return;
                }
                if (remCount === 0 && Math.abs(totalPct - 100) > 0.01) {
                    setSubmitError("Percentages must total 100%. Current total: " + totalPct.toFixed(1) + "%.");
                    return;
                }
                if (remCount > 0 && totalPct >= 100) {
                    setSubmitError("Percentage splits total " + totalPct.toFixed(1) + "% — leave room for Remaining Balance.");
                    return;
                }
            }
            setIsSubmitting(true);
            try {
                const newEmp = await createEmployee(payload);
                const newId = newEmp?.id || newEmp?.employeeId;
                const createdBankIds = [];
                if (paymentMethod === "direct_deposit" && newId) {
                    for (const acct of bankAccounts) {
                        if (acct.accountNumber && acct.routingNumber) {
                            const created = await createEmployeeBankAccount(newId, {
                                accountType: acct.accountType || "checking",
                                accountNumber: acct.accountNumber,
                                routingNumber: acct.routingNumber,
                            });
                            createdBankIds.push(created?.id || created?.bankAccountId);
                        }
                    }
                    if (createdBankIds.length >= 2 && addModeSplits.length >= 2) {
                        const splitsToSave = addModeSplits.slice(0, createdBankIds.length).map((s, i) => ({
                            bankAccountId: createdBankIds[i],
                            splitType: s.splitType || "remaining_balance",
                            value: s.value,
                        }));
                        await createEmployeeNetPaySplit(newId, splitsToSave);
                    }
                }
                queryClient.invalidateQueries({ queryKey: ["employees"] });
                if (sendInvite && newId) {
                    try {
                        const inv = await inviteEmployee(newId);
                        setInviteResult(inv?.alreadyExists ? 'exists' : 'sent');
                    } catch {
                        setInviteResult('error');
                    }
                }
                const isPendingOnboarding = fromOnboarding ||
                    existingEmployee?.onboardStatus === "pending_review" ||
                    existingEmployee?.onboardingStatus === "pending_review" ||
                    prefillData?.onboardStatus === "pending_review" ||
                    prefillData?.onboardingStatus === "pending_review";
                navigate(isPendingOnboarding ? "/onboarding" : "/employees");
            } catch (err) {
                setSubmitError(err?.response?.data?.message || "Failed to save employee. Please try again.");
            } finally {
                setIsSubmitting(false);
            }
        }
    }, [isEditMode, employeeId, mutateUpdate, buildPayload, paymentMethod, bankAccounts, addModeSplits, queryClient, navigate, sendInvite]);

    const updateBankAccount = (idx, field, value) =>
        setBankAccounts(prev => prev.map((a, i) => i === idx ? { ...a, [field]: value } : a));
    const addBankAccount = () => {
        setBankAccounts(prev => [...prev, emptyBankAccount()]);
        setAddModeSplits(prev => prev.length === 0
            ? [{ splitType: "percentage", value: "50" }, { splitType: "remaining_balance", value: null }]
            : [...prev, { splitType: "remaining_balance", value: null }]);
    };
    const removeBankAccount = (idx) => {
        setBankAccounts(prev => prev.filter((_, i) => i !== idx));
        setAddModeSplits(prev => prev.filter((_, i) => i !== idx));
    };
    const updateAddModeSplit = (idx, field, value) =>
        setAddModeSplits(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));

    const handleAddExistingBankAccount = async () => {
        if (!newBankAccount.accountNumber || !newBankAccount.routingNumber) return;
        try {
            await createEmployeeBankAccount(employeeId, { accountType: newBankAccount.accountType, accountNumber: newBankAccount.accountNumber, routingNumber: newBankAccount.routingNumber }, effectiveCid);
            setNewBankAccount(emptyBankAccount());
            setShowAddBankForm(false);
            refetchBankAccounts();
        } catch (err) {
            const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Unknown error';
            toast("Failed to add bank account: " + msg, 'error');
            console.error("Bank account error:", err?.response?.data || err);
        }
    };

    // Track the 4 admin-required fields live so highlights clear as soon as user fills them
    const watchedWage         = watch(EmployeeInputs.WAGE);
    const watchedPayType      = watch(EmployeeInputs.PAY_TYPE);
    const watchedFLSA         = watch(EmployeeInputs.OVERTIME_EXEMPT_STATUS);
    const watchedWorkLoc      = watch(EmployeeInputs.WORK_LOCATION);
    const watchedTimeZone     = watch(EmployeeInputs.WORK_TIME_ZONE);
    const watchedEmpType      = watch(EmployeeInputs.EMPLOYMENT_TYPE);
    const watchedAddressState = watch(EmployeeInputs.STATE);

    // Auto-fill timezone from the selected workplace
    const isFormReady = useRef(false);
    useEffect(() => {
        if (!isFormReady.current) { isFormReady.current = true; return; }
        if (!watchedWorkLoc || !workplacesList.length) return;
        const wp = workplacesList.find(w => w.id === watchedWorkLoc);
        if (wp?.timeZone || wp?.timezone) {
            setValue(EmployeeInputs.WORK_TIME_ZONE, wp.timeZone || wp.timezone, { shouldValidate: true, shouldDirty: true });
        }
    }, [watchedWorkLoc]); // eslint-disable-line react-hooks/exhaustive-deps

    // Detect address state change and prompt to sync tax withholding fields
    const [stateSyncPrompt, setStateSyncPrompt] = useState(null); // holds the new state code to sync
    const [stateSyncLocalTax, setStateSyncLocalTax] = useState(''); // auto-derived local tax area
    const prevAddressState = useRef(null);
    const addressStateReady = useRef(false);
    useEffect(() => {
        if (!watchedAddressState) return;
        if (!addressStateReady.current) {
            addressStateReady.current = true;
            prevAddressState.current = watchedAddressState;
            return;
        }
        const prev = prevAddressState.current;
        prevAddressState.current = watchedAddressState;
        if (prev === watchedAddressState) return;
        const currentResidentState = getValues(EmployeeInputs.RESIDENT_STATE);
        if (currentResidentState !== watchedAddressState) {
            setStateSyncPrompt(watchedAddressState);
            setStateSyncLocalTax('');
        }
    }, [watchedAddressState]); // eslint-disable-line react-hooks/exhaustive-deps

    const deriveLocalTaxArea = (state, city, county) => {
        const s  = (state  || '').toUpperCase().trim();
        const ci = (city   || '').toUpperCase().trim();
        const co = (county || '').toUpperCase().replace(/\s*COUNTY\s*$/, '').replace(/'/g, '').trim();

        if (s === 'MD') {
            if (ci === 'BALTIMORE') return 'BALTIMORE CITY';
            const mdMap = {
                'PRINCE GEORGES': 'PRINCE GEORGES',
                'PRINCE GEORGE': 'PRINCE GEORGES',
                'MONTGOMERY': 'MONTGOMERY',
                'BALTIMORE': 'BALTIMORE COUNTY',
                'ANNE ARUNDEL': 'ANNE ARUNDEL',
                'HOWARD': 'HOWARD',
                'FREDERICK': 'FREDERICK',
                'CARROLL': 'CARROLL',
                'HARFORD': 'HARFORD',
                'CHARLES': 'CHARLES',
            };
            return mdMap[co] || co || '';
        }
        if (s === 'NY') {
            const nycAreas = ['NEW YORK', 'BRONX', 'BROOKLYN', 'QUEENS', 'STATEN ISLAND', 'MANHATTAN', 'NEW YORK CITY'];
            if (nycAreas.some(b => ci.includes(b) || co.includes(b))) return 'NYC';
            return '';
        }
        if (s === 'OH') {
            const ohCities = ['COLUMBUS', 'CLEVELAND', 'CINCINNATI', 'TOLEDO', 'AKRON'];
            return ohCities.find(c => ci.includes(c)) || '';
        }
        if (s === 'PA') {
            const paCities = ['PHILADELPHIA', 'PITTSBURGH'];
            return paCities.find(c => ci.includes(c)) || '';
        }
        return '';
    };

    const handleStateSyncYes = (localTaxArea = '') => {
        if (!stateSyncPrompt) return;
        setValue(EmployeeInputs.RESIDENT_STATE, stateSyncPrompt, { shouldValidate: true });
        setValue(EmployeeInputs.WORK_STATE, stateSyncPrompt, { shouldValidate: true });
        setValue(EmployeeInputs.PRIMARY_WORK_STATE, stateSyncPrompt, { shouldValidate: true });
        setValue(EmployeeInputs.LOCAL_TAX_AREA, localTaxArea, { shouldValidate: false });
        setStateSyncPrompt(null);
    };

    // Highlight ALL empty fields and scroll to the first one when arriving from Complete Setup
    useEffect(() => {
        if (!highlightSetup) return;
        if (isEditMode && !existingEmployee && !prefillData) return;

        const HIGHLIGHT_CLS = 'dom-field-needs-setup';
        const HIGHLIGHT_STYLE = {
            borderRadius: '8px', border: '2px solid #1B75F0',
            background: '#EFF6FF', padding: '8px 8px 4px'
        };

        trigger(); // fire yup validation so inline errors show on all empty required fields

        const applyHighlights = () => {
            const form = document.querySelector('form');
            if (!form) return;

            // Clear previous DOM highlights
            form.querySelectorAll('.' + HIGHLIGHT_CLS).forEach(el => {
                el.classList.remove(HIGHLIGHT_CLS, 'field-needs-setup');
                Object.assign(el.style, { border: '', background: '', borderRadius: '', padding: '' });
            });

            const SKIP_NAMES = new Set([
                'termination_date',
                // already handled by reactive hl() wrappers - skip to avoid double border
                'wage', 'pay_type', 'overtime_exempt_status', 'work_location', 'work_time_zone', 'employment_type'
            ]);

            // Highlight every empty input/select wrapper
            form.querySelectorAll(
                'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]), select'
            ).forEach(field => {
                if (SKIP_NAMES.has(field.name)) return;
                if (!field.value || field.value === 'Select option') {
                    const wrapper = field.closest('[class*="col-lg"]') || field.parentElement;
                    if (wrapper && !wrapper.classList.contains(HIGHLIGHT_CLS)) {
                        wrapper.classList.add(HIGHLIGHT_CLS, 'field-needs-setup');
                        Object.assign(wrapper.style, HIGHLIGHT_STYLE);
                    }
                }
            });

            // Scroll to first empty field
            const first = form.querySelector('.field-needs-setup');
            if (first) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
        };

        const timer = setTimeout(applyHighlights, 500);

        // Clear highlight as user fills each field
        const handleInput = (e) => {
            if (e.target.value && e.target.value !== 'Select option') {
                const wrapper = e.target.closest('[class*="col-lg"]') || e.target.parentElement;
                if (wrapper) {
                    wrapper.classList.remove(HIGHLIGHT_CLS, 'field-needs-setup');
                    Object.assign(wrapper.style, { border: '', background: '', borderRadius: '', padding: '' });
                }
            }
        };

        const form = document.querySelector('form');
        if (form) {
            form.addEventListener('input', handleInput);
            form.addEventListener('change', handleInput);
        }

        return () => {
            clearTimeout(timer);
            if (form) {
                form.removeEventListener('input', handleInput);
                form.removeEventListener('change', handleInput);
            }
        };
    }, [highlightSetup, existingEmployee, isEditMode, trigger]);

    if (isEditMode && isLoadingEmployee) {
        return <div className="content" style={{ padding: "16px 24px" }}><p>Loading employee data...</p></div>;
    }

    // Returns a highlight wrapper style when `highlight=setup` is in URL and the field is empty
    const hl = (hasValue) => highlightSetup && !hasValue
        ? { borderRadius: 8, border: '2px solid #1B75F0', background: '#EFF6FF', padding: '8px 8px 4px' }
        : {};
    const hlClass = (hasValue) => (highlightSetup && !hasValue) ? 'field-needs-setup' : '';

    return (
        <form
            className={cn(styles["add-edit-employee-container"])}
            onSubmit={handleSubmit(onSubmit)}
        >
            <h3 className={styles["title"]}>{isEditMode ? "Edit Employee" : "Add Employee"}</h3>

            {/* Pending onboarding banner — shown when editing a pending_review employee */}
            {isEditMode && (existingEmployee?.onboardStatus === "pending_review" || existingEmployee?.onboardingStatus === "pending_review" || prefillData?.onboardStatus === "pending_review" || prefillData?.onboardingStatus === "pending_review") && (
                <div style={{ display:'flex', alignItems:'flex-start', gap:12, background:'#EFF6FF', border:'2px solid #1B75F0', borderRadius:10, padding:'12px 16px', marginBottom:20 }}>
                    <span style={{ fontSize:20 }}>👤</span>
                    <div>
                        <div style={{ fontWeight:700, color:'#c2410c', fontSize:14 }}>Pending Onboarding — Review &amp; Save</div>
                        <div style={{ fontSize:13, color:'#7c2d12', marginTop:2 }}>
                            This employee is awaiting onboarding approval. Fill in any missing details, then click <strong>Update Employee</strong>.
                            You will be returned to the <strong>Pending Onboardings</strong> page to approve them.
                        </div>
                    </div>
                </div>
            )}

            {/* Setup guidance banner — shown when arriving from "Complete Setup" button */}
            {highlightSetup && (
                <div style={{ display:'flex', alignItems:'flex-start', gap:12, background:'#EFF6FF', border:'2px solid #1B75F0', borderRadius:10, padding:'12px 16px', marginBottom:20 }}>
                    <span style={{ fontSize:20 }}>⚙️</span>
                    <div>
                        <div style={{ fontWeight:700, color:'#c2410c', fontSize:14 }}>Complete Employee Setup</div>
                        <div style={{ fontSize:13, color:'#7c2d12', marginTop:2 }}>
                            The fields highlighted in blue below need to be filled before this employee can be included in payroll.
                            Highlights disappear as you fill each field.
                        </div>
                    </div>
                </div>
            )}

            <div className={styles["personal-info-container"]}>
                <p className={styles["info-title"]}>Personal Information</p>
                <div className={cn(styles["personal-info-form"], "")}>
                    <div className="row g-3">
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="First Name" required variant="flat"
                                {...register(EmployeeInputs.FIRSTNAME)}
                                error={!!errors[EmployeeInputs.FIRSTNAME]}
                                errorText={errors[EmployeeInputs.FIRSTNAME]?.message} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Middle Name" variant="flat" containerClasses="col"
                                {...register(EmployeeInputs.MIDDLENAME)}
                                error={!!errors[EmployeeInputs.MIDDLENAME]}
                                errorText={errors[EmployeeInputs.MIDDLENAME]?.message} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Last Name" required variant="flat" containerClasses="col"
                                {...register(EmployeeInputs.LASTNAME)}
                                error={!!errors[EmployeeInputs.LASTNAME]}
                                errorText={errors[EmployeeInputs.LASTNAME]?.message} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Social Security Number" required variant="flat" containerClasses="col"
                                {...register(EmployeeInputs.SOCIAL_SECURITY_NUMBER)}
                                onChange={e => {
                                    const digits = e.target.value.replace(/\D/g, '').slice(0, 9);
                                    let formatted = digits;
                                    if (digits.length > 5) formatted = digits.slice(0,3) + '-' + digits.slice(3,5) + '-' + digits.slice(5);
                                    else if (digits.length > 3) formatted = digits.slice(0,3) + '-' + digits.slice(3);
                                    setValue(EmployeeInputs.SOCIAL_SECURITY_NUMBER, formatted, { shouldValidate: true });
                                }}
                                maxLength={11}
                                placeholder="###-##-####"
                                error={!!errors[EmployeeInputs.SOCIAL_SECURITY_NUMBER]}
                                errorText={errors[EmployeeInputs.SOCIAL_SECURITY_NUMBER]?.message} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Birth Date" required variant="flat" type="date" containerClasses="col"
                                {...register(EmployeeInputs.BIRTH_DATE)}
                                error={!!errors[EmployeeInputs.BIRTH_DATE]}
                                errorText={errors[EmployeeInputs.BIRTH_DATE]?.message} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Select fullWidth label="Marital Status" placeholder="Select status"
                                options={[{ label: "Married", value: "Married" }, { label: "Unmarried", value: "Unmarried" }]}
                                {...register(EmployeeInputs.MARITAL_STATUS)}
                                error={!!errors[EmployeeInputs.MARITAL_STATUS]}
                                errorText={errors[EmployeeInputs.MARITAL_STATUS]?.message} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Preferred Name" variant="flat"
                                {...register(EmployeeInputs.PREFERRED_NAME)}
                                error={!!errors[EmployeeInputs.PREFERRED_NAME]}
                                errorText={errors[EmployeeInputs.PREFERRED_NAME]?.message} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Job Title" variant="flat"
                                {...register(EmployeeInputs.JOB_TITLE)}
                                error={!!errors[EmployeeInputs.JOB_TITLE]}
                                errorText={errors[EmployeeInputs.JOB_TITLE]?.message} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Select fullWidth label="Department" placeholder="Select department"
                                options={departmentOptions}
                                value={watch(EmployeeInputs.DEPARTMENT)}
                                {...register(EmployeeInputs.DEPARTMENT)}
                                error={!!errors[EmployeeInputs.DEPARTMENT]}
                                errorText={errors[EmployeeInputs.DEPARTMENT]?.message} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <div style={hl(watchedEmpType)} className={hlClass(watchedEmpType)}>
                                <Select fullWidth label="Employment Type" required placeholder="Select type"
                                    options={[
                                        { label: "Full-time", value: "Full-time" },
                                        { label: "Part-time", value: "Part-time" },
                                        { label: "Contract", value: "Contract" },
                                        { label: "Temporary", value: "Temporary" },
                                        { label: "Seasonal", value: "Seasonal" },
                                        { label: "Intern", value: "Intern" },
                                    ]}
                                    {...register(EmployeeInputs.EMPLOYMENT_TYPE)}
                                    error={!!errors[EmployeeInputs.EMPLOYMENT_TYPE]}
                                    errorText={errors[EmployeeInputs.EMPLOYMENT_TYPE]?.message} />
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <AddressAutocomplete
                                label="Street Address"
                                required
                                value={watch(EmployeeInputs.ADDRESS) || ''}
                                onSelect={(a) => {
                                    setValue(EmployeeInputs.ADDRESS, a.line1, { shouldValidate: true });
                                    setValue(EmployeeInputs.CITY,    a.city,  { shouldValidate: true });
                                    setValue(EmployeeInputs.STATE,   a.state, { shouldValidate: true });
                                    setValue(EmployeeInputs.ZIP_CODE, a.zip,  { shouldValidate: true });
                                    const derived = deriveLocalTaxArea(a.state, a.city, a.county);
                                    setValue(EmployeeInputs.LOCAL_TAX_AREA, derived, { shouldValidate: false });
                                    const currentResidentState = getValues(EmployeeInputs.RESIDENT_STATE);
                                    if (a.state && currentResidentState && currentResidentState !== a.state) {
                                        setStateSyncLocalTax(derived);
                                        setStateSyncPrompt(a.state);
                                    }
                                }}
                                onChange={(v) => setValue(EmployeeInputs.ADDRESS, v, { shouldValidate: true })}
                                error={!!errors[EmployeeInputs.ADDRESS]}
                                errorText={errors[EmployeeInputs.ADDRESS]?.message}
                            />
                        </div>
                    </div>
                    <div className="row g-3 mt-3">
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="City" required variant="flat"
                                {...register(EmployeeInputs.CITY)}
                                error={!!errors[EmployeeInputs.CITY]}
                                errorText={errors[EmployeeInputs.CITY]?.message} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Select fullWidth label="State" required placeholder="Select state" options={US_STATES}
                                {...register(EmployeeInputs.STATE)}
                                error={!!errors[EmployeeInputs.STATE]}
                                errorText={errors[EmployeeInputs.STATE]?.message} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Zip Code" required variant="flat"
                                {...register(EmployeeInputs.ZIP_CODE)}
                                error={!!errors[EmployeeInputs.ZIP_CODE]}
                                errorText={errors[EmployeeInputs.ZIP_CODE]?.message} />
                        </div>
                        {stateSyncPrompt && (
                            <div className="col-12">
                                <div style={{ background: "#fff8e1", border: "1px solid #f9a825", borderRadius: 8, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                                    <span style={{ fontSize: 13, color: "#5d4037", flex: 1 }}>
                                        <strong>Address state changed to {stateSyncPrompt}.</strong> Update Resident State and Work State to {stateSyncPrompt}
                                        {stateSyncLocalTax ? <>, and set Local Tax Area to <strong>{stateSyncLocalTax}</strong></> : ', and clear Local Tax Area'}?
                                    </span>
                                    <button type="button" onClick={() => handleStateSyncYes(stateSyncLocalTax)}
                                        style={{ background: "#f9a825", border: "none", borderRadius: 6, padding: "6px 16px", fontWeight: 600, fontSize: 13, cursor: "pointer", color: "#fff" }}>
                                        Yes, update tax fields
                                    </button>
                                    <button type="button" onClick={() => setStateSyncPrompt(null)}
                                        style={{ background: "transparent", border: "1px solid #bbb", borderRadius: 6, padding: "6px 14px", fontSize: 13, cursor: "pointer", color: "#666" }}>
                                        No, keep as-is
                                    </button>
                                </div>
                            </div>
                        )}
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Email" required variant="flat"
                                {...register(EmployeeInputs.EMAIL)}
                                error={!!errors[EmployeeInputs.EMAIL]}
                                errorText={errors[EmployeeInputs.EMAIL]?.message} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Home Phone" variant="flat"
                                {...register(EmployeeInputs.HOME_PHONE)}
                                onChange={e => {
                                    const d = e.target.value.replace(/\D/g, '').slice(0, 10);
                                    const fmt = d.length <= 3 ? (d.length ? `(${d}` : '') : d.length <= 6 ? `(${d.slice(0,3)}) ${d.slice(3)}` : `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
                                    setValue(EmployeeInputs.HOME_PHONE, fmt, { shouldValidate: true });
                                }}
                                maxLength={14}
                                placeholder="(555) 555-5555"
                                error={!!errors[EmployeeInputs.HOME_PHONE]}
                                errorText={errors[EmployeeInputs.HOME_PHONE]?.message} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Cell Phone" required variant="flat"
                                {...register(EmployeeInputs.CELL_PHONE)}
                                onChange={e => {
                                    const d = e.target.value.replace(/\D/g, '').slice(0, 10);
                                    const fmt = d.length <= 3 ? (d.length ? `(${d}` : '') : d.length <= 6 ? `(${d.slice(0,3)}) ${d.slice(3)}` : `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
                                    setValue(EmployeeInputs.CELL_PHONE, fmt, { shouldValidate: true });
                                }}
                                maxLength={14}
                                placeholder="(555) 555-5555"
                                error={!!errors[EmployeeInputs.CELL_PHONE]}
                                errorText={errors[EmployeeInputs.CELL_PHONE]?.message} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Start Date" required variant="flat" type="date"
                                {...register(EmployeeInputs.START_DATE)}
                                error={!!errors[EmployeeInputs.START_DATE]}
                                errorText={errors[EmployeeInputs.START_DATE]?.message} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Termination Date" variant="flat" type="date"
                                {...register(EmployeeInputs.TERMINATION_DATE)}
                                error={!!errors[EmployeeInputs.TERMINATION_DATE]}
                                errorText={errors[EmployeeInputs.TERMINATION_DATE]?.message} />
                        </div>
                    </div>
                </div>
            </div>
            <div className={cn(styles["eligibilty-info-container"], "mt-4")}>
                <p className={styles["info-title"]}>Employment Status</p>
                <div className={cn(styles["eligibilty-info-form"], "container-fluid")}>
                    <div className="row">
                        <div className="col-lg-4 col-md-6 col-12">
                            <Select fullWidth label="Active Status"
                                options={[{ label: "Active", value: "true" }, { label: "Inactive", value: "false" }]}
                                {...register(EmployeeInputs.ACTIVE_STATUS)}
                                error={!!errors[EmployeeInputs.ACTIVE_STATUS]}
                                errorText={errors[EmployeeInputs.ACTIVE_STATUS]?.message} />
                        </div>
                    </div>
                </div>
            </div>
            <div className={cn(styles["work-info-container"], "mt-4")}>
                <p className={styles["info-title"]}>Work Information</p>
                <div className={cn(styles["work-info-form"], "container-fluid")}>
                    <h4 className={styles["label"]}>Primarily Work From Home</h4>
                    <div className={styles["radio-group"]}>
                        <Radio name="workfrom-hom-group" value="yes" label="Yes" {...register(EmployeeInputs.WORK_FROM_HOME)} />
                        <Radio name="workfrom-hom-group" value="no" label="No" {...register(EmployeeInputs.WORK_FROM_HOME)} />
                    </div>
                    <div className="row mt-3">
                        <div className="col-lg-4 col-md-6 col-12">
                            <div style={hl(watchedWorkLoc)} className={hlClass(watchedWorkLoc)}>
                                <Select fullWidth label="Work Location"
                                    placeholder="Select work location" options={workplaceOptions}
                                    value={watch(EmployeeInputs.WORK_LOCATION)}
                                    {...register(EmployeeInputs.WORK_LOCATION)}
                                    error={!!errors[EmployeeInputs.WORK_LOCATION]}
                                    errorText={errors[EmployeeInputs.WORK_LOCATION]?.message} />
                            </div>
                        </div>
                        {requireLocation && (
                        <div className="col-lg-4 col-md-6 col-12">
                            <Select fullWidth label="Branch / Location *"
                                placeholder="Assign to location" options={locationOptions}
                                value={watch(EmployeeInputs.LOCATION_ID)}
                                disabled={!!lockedLocationId}
                                {...register(EmployeeInputs.LOCATION_ID)}
                                error={!!errors[EmployeeInputs.LOCATION_ID]}
                                errorText={errors[EmployeeInputs.LOCATION_ID]?.message} />
                            {lockedLocationId && (
                                <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>Your login is limited to this location.</p>
                            )}
                        </div>
                        )}
                        <div className="col-lg-4 col-md-6 col-12">
                            <div style={hl(watchedTimeZone)} className={hlClass(watchedTimeZone)}>
                                <Select fullWidth label="Work Time Zone"
                                    placeholder="Select time zone" options={US_TIME_ZONES}
                                    {...register(EmployeeInputs.WORK_TIME_ZONE)}
                                    error={!!errors[EmployeeInputs.WORK_TIME_ZONE]}
                                    errorText={errors[EmployeeInputs.WORK_TIME_ZONE]?.message} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className={cn(styles["wage-info-container"], "mt-4")}>
                <p className={styles["info-title"]}>Wage Information</p>
                <div className={cn(styles["wage-info-form"], "container-fluid")}>
                    <div className="row">
                        <div className="col-lg-4 col-md-6 col-12">
                            <div style={hl(watchedWage && Number(watchedWage) > 0)} className={hlClass(watchedWage && Number(watchedWage) > 0)}>
                                <Input fullWidth label="Wage" required variant="flat"
                                    {...register(EmployeeInputs.WAGE)}
                                    error={!!errors[EmployeeInputs.WAGE]}
                                    errorText={errors[EmployeeInputs.WAGE]?.message} />
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <div style={hl(watchedPayType)} className={hlClass(watchedPayType)}>
                                <Select fullWidth label="Pay Type" required placeholder="Select pay type"
                                    options={[{ label: "Salary (annual)", value: "salary" }, { label: "Hourly", value: "hourly" }]}
                                    {...register(EmployeeInputs.PAY_TYPE)}
                                    error={!!errors[EmployeeInputs.PAY_TYPE]}
                                    errorText={errors[EmployeeInputs.PAY_TYPE]?.message} />
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Full-Time Hours / Week" variant="flat" type="number" placeholder="40"
                                {...register(EmployeeInputs.FULL_TIME_HOURS)}
                                error={!!errors[EmployeeInputs.FULL_TIME_HOURS]}
                                errorText={errors[EmployeeInputs.FULL_TIME_HOURS]?.message} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Select fullWidth label="Pay Schedule"
                                placeholder="Select pay schedule"
                                options={payScheduleOptions}
                                {...register(EmployeeInputs.PAYMENT_SCHEDULE_ID)}
                                error={!!errors[EmployeeInputs.PAYMENT_SCHEDULE_ID]}
                                errorText={errors[EmployeeInputs.PAYMENT_SCHEDULE_ID]?.message} />
                        </div>
                    </div>
                    <div className="mt-4 row">
                        <div className="col-lg-4 col-md-6 col-12">
                            <div style={hl(watchedFLSA)} className={hlClass(watchedFLSA)}>
                                <Select fullWidth label="FLSA Overtime Exempt Status" required
                                    options={[{ label: "Exempt", value: "Exempt" }, { label: "Non-Exempt", value: "Non-Exempt" }]}
                                    value={watch(EmployeeInputs.OVERTIME_EXEMPT_STATUS)}
                                    {...register(EmployeeInputs.OVERTIME_EXEMPT_STATUS)}
                                    error={!!errors[EmployeeInputs.OVERTIME_EXEMPT_STATUS]}
                                    errorText={errors[EmployeeInputs.OVERTIME_EXEMPT_STATUS]?.message} />
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Select fullWidth label="Supplement Tax Method"
                                options={[{ label: "Flat", value: "Flat" }, { label: "Aggregate", value: "Aggregate" }]}
                                value={watch(EmployeeInputs.SUPPLEMENT_TAX_METHOD)}
                                {...register(EmployeeInputs.SUPPLEMENT_TAX_METHOD)}
                                error={!!errors[EmployeeInputs.SUPPLEMENT_TAX_METHOD]}
                                errorText={errors[EmployeeInputs.SUPPLEMENT_TAX_METHOD]?.message} />
                        </div>
                    </div>
                </div>
            </div>
            <div className={cn(styles["wage-info-container"], "mt-4")}>
                <p className={styles["info-title"]}>Federal Tax Withholding (W-4)</p>
                <div className="container-fluid">
                    <div className="row g-3">
                        <div className="col-lg-4 col-md-6 col-12">
                            <Select fullWidth label="Federal Filing Status" required placeholder="Select filing status"
                                options={[
                                    { label: "Single / Married Filing Separately", value: "S" },
                                    { label: "Married Filing Jointly", value: "MFJ" },
                                    { label: "Head of Household", value: "HOH" },
                                ]}
                                {...register(EmployeeInputs.FEDERAL_FILING_STATUS)} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Additional Withholding ($)" variant="flat" type="number" placeholder="0"
                                {...register(EmployeeInputs.FEDERAL_EXTRA_WITHHOLDING)} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12 d-flex align-items-center pt-3">
                            <Checkbox label="Exempt from Federal Income Tax Withholding"
                                {...register(EmployeeInputs.FEDERAL_EXEMPT)} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Select fullWidth label="Multiple Jobs or Spouse Works" placeholder="Select option"
                                options={[{ label: "Yes", value: "Yes" }, { label: "No", value: "No" }]}
                                {...register(EmployeeInputs.MULTIPLE_JOBS)} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Qualifying Dependents Amount ($)" variant="flat" type="number" placeholder="0"
                                {...register(EmployeeInputs.DEPENDENTS_AMOUNT)} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Other Income ($)" variant="flat" type="number" placeholder="0"
                                {...register(EmployeeInputs.OTHER_INCOME)} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Deductions ($)" variant="flat" type="number" placeholder="0"
                                {...register(EmployeeInputs.DEDUCTIONS_AMOUNT)} />
                        </div>
                    </div>
                    <div className="row g-3 mt-2">
                        <div className="col-lg-4 col-md-6 col-12">
                            <Select fullWidth label="State Filing Status" required placeholder="Select status"
                                options={[
                                    { label: "Single", value: "S" },
                                    { label: "Married Filing Jointly", value: "MFJ" },
                                    { label: "Head of Household", value: "HOH" },
                                ]}
                                {...register(EmployeeInputs.STATE_FILING_STATUS)} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="State Allowances" variant="flat" type="number" min="0" max="99"
                                placeholder="0"
                                {...register(EmployeeInputs.STATE_ALLOWANCES)} />
                            <p style={{ fontSize: 11, color: '#888', marginTop: 3 }}>
                                Number of allowances claimed on state W-4 (leave 0 if state uses flat withholding).
                            </p>
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Select fullWidth label="Resident State" required placeholder="Select state" options={US_STATES}
                                {...register(EmployeeInputs.RESIDENT_STATE)} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Select fullWidth label="Work State" required placeholder="Select state" options={US_STATES}
                                {...register(EmployeeInputs.WORK_STATE)} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Local Tax Area" variant="flat" placeholder="e.g. MONTGOMERY, NYC, COLUMBUS"
                                {...register(EmployeeInputs.LOCAL_TAX_AREA)} />
                        </div>
                    </div>
                    <p className="mt-2 mb-0" style={{ fontSize: 12, color: "#888" }}>
                        <strong>Local Tax Area</strong> — Enter the locality code for local income tax withholding. Leave blank if not applicable.
                        Valid values: <strong>MD counties:</strong> MONTGOMERY, PRINCE GEORGES, BALTIMORE CITY, HOWARD, ANNE ARUNDEL, FREDERICK, CARROLL, HARFORD, CHARLES, BALTIMORE COUNTY &nbsp;|&nbsp;
                        <strong>NY:</strong> NYC &nbsp;|&nbsp;
                        <strong>OH cities:</strong> COLUMBUS, CLEVELAND, CINCINNATI, TOLEDO, AKRON &nbsp;|&nbsp;
                        <strong>PA localities:</strong> PHILADELPHIA, PITTSBURGH
                    </p>
                    <p className="mt-1 mb-0" style={{ fontSize: 12, color: "#888" }}>
                        Filing status and withholding are used to calculate Federal Income Tax (FITW) during payroll processing.
                    </p>
                </div>
            </div>
            {/* Payment Method */}
            <div className={cn(styles["wage-info-container"], "mt-4")}>
                <p className={styles["info-title"]}>Payment Method</p>
                <p style={{ fontSize: 13, color: "#666", marginBottom: 16 }}>
                    Employees with a bank account on file are paid via <strong>Direct Deposit</strong>. Without a bank account they are paid by <strong>Check</strong>.
                </p>
                {isEditMode ? (
                    <div>
                        {loadingBankAccounts && <p style={{ fontSize: 13, color: "#888" }}>Loading payment info...</p>}
                        {existingBankAccounts.map(acct => (
                            <div key={acct.id} className="p-3 mb-3" style={{ border: "1px solid #dee2e6", borderRadius: 8, background: "#fafafa" }}>
                                {editingBankAccountId === acct.id ? (
                                    <div>
                                        <strong style={{ fontSize: 14 }}>Edit Bank Account</strong>
                                        <div className="row g-3 mt-1">
                                            <div className="col-lg-4 col-md-6 col-12">
                                                <Select fullWidth label="Account Type"
                                                    options={[{ label: "Checking", value: "checking" }, { label: "Savings", value: "savings" }]}
                                                    value={editBankAccountData.accountType || acct.accountType || "checking"}
                                                    onChange={(e) => setEditBankAccountData(p => ({ ...p, accountType: e.target.value }))} />
                                            </div>
                                            <div className="col-lg-4 col-md-6 col-12">
                                                <Input fullWidth label="Account Number" variant="flat"
                                                    value={editBankAccountData.accountNumber ?? acct.accountNumber ?? ""}
                                                    onChange={(e) => setEditBankAccountData(p => ({ ...p, accountNumber: e.target.value }))} />
                                            </div>
                                            <div className="col-lg-4 col-md-6 col-12">
                                                <Input fullWidth label="Routing Number" variant="flat"
                                                    value={editBankAccountData.routingNumber ?? acct.routingNumber ?? ""}
                                                    onChange={(e) => setEditBankAccountData(p => ({ ...p, routingNumber: e.target.value }))} />
                                            </div>
                                        </div>
                                        <div className="mt-3 d-flex gap-2">
                                            <button type="button" className="btn btn-sm" style={{ backgroundColor: "#1B75F0", color: "#fff" }}
                                                onClick={async () => {
                                                    try {
                                                        await updateEmployeeBankAccount(employeeId, acct.id, editBankAccountData, effectiveCid);
                                                        setEditingBankAccountId(null); setEditBankAccountData({});
                                                        refetchBankAccounts();
                                                    } catch (e) { toast("Failed to update account.", 'error'); }
                                                }}>Save</button>
                                            <button type="button" className="btn btn-sm btn-outline-secondary"
                                                onClick={() => { setEditingBankAccountId(null); setEditBankAccountData({}); }}>Cancel</button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <span className="fw-semibold" style={{ fontSize: 13 }}>Bank Account</span>
                                            <span className="ms-2 badge" style={{ background: "#d4edda", color: "#155724", fontSize: 11 }}>Direct Deposit</span>
                                            <div style={{ fontSize: 12, color: "#555", marginTop: 4 }}>
                                                {acct.accountType ? acct.accountType.charAt(0).toUpperCase() + acct.accountType.slice(1) : "Savings"} &mdash; {maskAccount(acct.accountNumber)} &nbsp; Routing: {acct.routingNumber}
                                            </div>
                                        </div>
                                        <div className="d-flex gap-2">
                                            <button type="button" className="btn btn-sm btn-outline-secondary"
                                                onClick={() => { setEditingBankAccountId(acct.id); setEditBankAccountData({ accountType: acct.accountType, accountNumber: acct.accountNumber, routingNumber: acct.routingNumber }); }}>Edit</button>
                                            {existingBankAccounts.length > 1 && (
                                                <button type="button" className="btn btn-sm btn-outline-danger"
                                                    onClick={() => setConfirmRemoveId(acct.id)}>Remove</button>
                                            )}
                                {confirmRemoveId === acct.id && (
                                    <div className="mt-2 p-2" style={{ background: "#fff3cd", border: "1px solid #ffc107", borderRadius: 6, fontSize: 13 }}>
                                        <p className="mb-2 fw-semibold" style={{ color: "#856404" }}>Remove this bank account?</p>
                                        <div className="d-flex gap-2">
                                            <button type="button" className="btn btn-sm btn-danger"
                                                onClick={async () => {
                                                    try {
                                                        await deleteEmployeeBankAccount(employeeId, acct.id, effectiveCid);
                                                        if (existingBankAccounts.length - 1 <= 1) setSplits([]);
                                                        setConfirmRemoveId(null);
                                                        refetchBankAccounts();
                                                    } catch (e) { setConfirmRemoveId(null); }
                                                }}>Yes, Remove</button>
                                            <button type="button" className="btn btn-sm btn-outline-secondary"
                                                onClick={() => setConfirmRemoveId(null)}>Cancel</button>
                                        </div>
                                    </div>
                                )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                        {!loadingBankAccounts && existingBankAccounts.length === 0 && (
                            <p style={{ fontSize: 13, color: "#888" }}>No bank account on file &mdash; employee will be paid by <strong>Check</strong>.</p>
                        )}
                        {showAddBankForm ? (
                            <div className="p-3 mt-3" style={{ border: "1px solid #dee2e6", borderRadius: 8, background: "#fafafa" }}>
                                <strong style={{ fontSize: 14 }}>New Bank Account</strong>
                                <div className="row g-3 mt-1">
                                    <div className="col-lg-4 col-md-6 col-12">
                                        <Select fullWidth label="Account Type"
                                            options={[{ label: "Checking", value: "checking" }, { label: "Savings", value: "savings" }]}
                                            value={newBankAccount.accountType}
                                            onChange={(e) => setNewBankAccount(p => ({ ...p, accountType: e.target.value }))} />
                                    </div>
                                    <div className="col-lg-4 col-md-6 col-12">
                                        <Input fullWidth label="Account Number" variant="flat"
                                            value={newBankAccount.accountNumber}
                                            onChange={(e) => setNewBankAccount(p => ({ ...p, accountNumber: e.target.value }))} />
                                    </div>
                                    <div className="col-lg-4 col-md-6 col-12">
                                        <Input fullWidth label="Routing Number" variant="flat"
                                            value={newBankAccount.routingNumber}
                                            onChange={(e) => setNewBankAccount(p => ({ ...p, routingNumber: e.target.value }))} />
                                    </div>
                                </div>
                                <div className="mt-3 d-flex gap-2">
                                    <button type="button" className="btn btn-sm" style={{ backgroundColor: "#1B75F0", color: "#fff" }}
                                        onClick={handleAddExistingBankAccount}>Save Account</button>
                                    <button type="button" className="btn btn-sm btn-outline-secondary"
                                        onClick={() => { setShowAddBankForm(false); setNewBankAccount(emptyBankAccount()); }}>Cancel</button>
                                </div>
                            </div>
                        ) : (
                            <div className="d-flex gap-2 mt-3 align-items-center flex-wrap">
                                <button type="button" className="btn btn-sm" style={{ backgroundColor: "#1B75F0", color: "#fff", borderRadius: 6 }}
                                    onClick={() => setShowAddBankForm(true)}>+ Add Bank Account</button>
                                {existingBankAccounts.length > 0 && !showSwitchToCheckConfirm && (
                                    <button type="button" className="btn btn-sm btn-outline-danger"
                                        onClick={() => setShowSwitchToCheckConfirm(true)}>Switch to Check</button>
                                )}
                                {showSwitchToCheckConfirm && (
                                    <div className="mt-2 p-3" style={{ background: "#fff3cd", border: "1px solid #ffc107", borderRadius: 8, fontSize: 13 }}>
                                        <p className="mb-2 fw-semibold" style={{ color: "#856404" }}>Switch to Check Payment?</p>
                                        <p className="mb-3" style={{ color: "#856404" }}>This will permanently remove all bank accounts. The employee will be paid by check going forward.</p>
                                        <div className="d-flex gap-2">
                                            <button type="button" className="btn btn-sm btn-danger"
                                                onClick={async () => {
                                                    try {
                                                        for (const acct of existingBankAccounts) { await deleteEmployeeBankAccount(employeeId, acct.id); }
                                                        setSplits([]); setShowSwitchToCheckConfirm(false); refetchBankAccounts();
                                                    } catch (e) { setShowSwitchToCheckConfirm(false); }
                                                }}>Yes, Switch to Check</button>
                                            <button type="button" className="btn btn-sm btn-outline-secondary"
                                                onClick={() => setShowSwitchToCheckConfirm(false)}>Cancel</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        {existingBankAccounts.length > 1 && (
                            <div className="mt-4">
                                <p className={styles["info-title"]} style={{ fontSize: 14 }}>Direct Deposit Allocation</p>
                                <table className="table table-sm" style={{ fontSize: 13 }}>
                                    <thead><tr><th>Account</th><th>Split Type</th><th>Value</th><th></th></tr></thead>
                                    <tbody>
                                        {splits.map((split, idx) => (
                                            <tr key={idx}>
                                                <td><select className="form-select form-select-sm" value={split.bankAccountId || ""}
                                                    onChange={(e) => setSplits(prev => prev.map((s, i) => i === idx ? { ...s, bankAccountId: e.target.value } : s))}>
                                                    <option value="">Select account</option>
                                                    {existingBankAccounts.map(a => (
                                                        <option key={a.id} value={a.id}>{a.accountType ? a.accountType.charAt(0).toUpperCase() + a.accountType.slice(1) : "Account"} ****{String(a.accountNumber || "").slice(-4)}</option>
                                                    ))}
                                                </select></td>
                                                <td><select className="form-select form-select-sm" value={split.splitType || "remaining_balance"}
                                                    onChange={(e) => {
                                                        const newType = e.target.value;
                                                        setSplits(prev => prev.map((s, i) =>
                                                            i === idx
                                                                ? { ...s, splitType: newType, value: newType === "remaining_balance" ? null : (s.value ?? null) }
                                                                : s
                                                        ));
                                                    }}>
                                                    <option value="remaining_balance">Select</option>
                                                    <option value="percentage">Percentage (%)</option>
                                                    <option value="flat">Flat Amount ($)</option>
                                                </select></td>
                                                <td>
                                                    {split.splitType !== "remaining_balance" ? (
                                                        <input type="number" className="form-control form-control-sm"
                                                            placeholder={split.splitType === "percentage" ? "e.g. 50" : "e.g. 500"}
                                                            value={split.value || ""}
                                                            onChange={(e) => setSplits(prev => prev.map((s, i) => i === idx ? { ...s, value: e.target.value } : s))} />
                                                    ) : (() => {
                                                        const usedPct = splits.filter((s, i) => i !== idx && s.splitType === "percentage").reduce((sum, s) => sum + (parseFloat(s.value) || 0), 0);
                                                        const rem = Math.max(0, 100 - usedPct);
                                                        return <input type="text" className="form-control form-control-sm" readOnly value={Math.round(rem)} style={{ background: "#f0f9f0", color: "#155724", fontWeight: 600, cursor: "default" }} />;
                                                    })()}
                                                </td>
                                                <td><button type="button" className="btn btn-sm btn-outline-danger"
                                                    onClick={() => setSplits(prev => prev.filter((_, i) => i !== idx))}>Remove</button></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <button type="button" className="btn btn-sm btn-outline-secondary"
                                    onClick={() => { const defType = splits.length > 0 ? splits.find(s => s.splitType !== "remaining_balance")?.splitType ?? "percentage" : "percentage"; setSplits(prev => [...prev, { bankAccountId: "", splitType: defType, value: null }]); }}>+ Add Split</button>
                                <button type="button" className="btn btn-sm ms-2" style={{ backgroundColor: "#1B75F0", color: "#fff" }}
                                    onClick={async () => {
                                        const missingAcct = splits.some(s => !s.bankAccountId);
                                        if (missingAcct) { setSplitMessage("error:Please select a bank account for each split."); return; }
                                        const remCount = splits.filter(s => s.splitType === "remaining_balance").length;
                                        if (remCount > 1) { setSplitMessage("error:Only one split can be Remaining Balance."); return; }
                                        const totalPct = splits.filter(s => s.splitType === "percentage").reduce((sum, s) => sum + (parseFloat(s.value) || 0), 0);
                                        const totalFlat = splits.filter(s => s.splitType === "flat").reduce((sum, s) => sum + (parseFloat(s.value) || 0), 0);
                                        if (remCount === 0 && splits.every(s => s.splitType === "percentage") && Math.abs(totalPct - 100) > 0.01) {
                                            setSplitMessage("error:Percentages must total 100%. Current total: " + totalPct.toFixed(1) + "%."); return;
                                        }
                                        if (remCount > 0 && totalPct >= 100) {
                                            setSplitMessage("error:Percentage splits total " + totalPct.toFixed(1) + "% — leave room for Remaining Balance."); return;
                                        }
                                        try {
                                            await createEmployeeNetPaySplit(employeeId, splits, effectiveCid);
                                            setSplitMessage("Allocations saved."); setTimeout(() => setSplitMessage(""), 3000);
                                        } catch (e) { setSplitMessage("error:Failed to save allocations."); }
                                    }}>Save Allocations</button>
                                {splitMessage && (
                                    <p className="mt-2" style={{ fontSize: 13, color: splitMessage.startsWith("error:") ? "#dc3545" : "#198754" }}>
                                        {splitMessage.startsWith("error:") ? splitMessage.slice(6) : splitMessage}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                ) : (
                    <div>
                        <div className="row g-3">
                            <div className="col-lg-4 col-md-6 col-12">
                                <Select fullWidth label="Payment Method"
                                    options={[{ label: "Check", value: "check" }, { label: "Direct Deposit", value: "direct_deposit" }]}
                                    value={paymentMethod}
                                    onChange={(e) => setPaymentMethod(e.target.value)} />
                            </div>
                        </div>
                        {paymentMethod === "direct_deposit" && (
                            <div className="mt-3">
                                {bankAccounts.map((acct, idx) => (
                                    <div key={idx} className="p-3 mb-3" style={{ border: "1px solid #dee2e6", borderRadius: 8, background: "#fafafa" }}>
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <strong style={{ fontSize: 14 }}>Bank Account {idx + 1}</strong>
                                            {idx > 0 && (<button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeBankAccount(idx)}>Remove</button>)}
                                        </div>
                                        <div className="row g-3">
                                            <div className="col-lg-4 col-md-6 col-12">
                                                <Select fullWidth label="Account Type"
                                                    options={[{ label: "Checking", value: "checking" }, { label: "Savings", value: "savings" }]}
                                                    value={acct.accountType} onChange={(e) => updateBankAccount(idx, "accountType", e.target.value)} />
                                            </div>
                                            <div className="col-lg-4 col-md-6 col-12">
                                                <Input fullWidth label="Account Number" variant="flat"
                                                    value={acct.accountNumber} onChange={(e) => updateBankAccount(idx, "accountNumber", e.target.value)} />
                                            </div>
                                            <div className="col-lg-4 col-md-6 col-12">
                                                <Input fullWidth label="Routing Number" variant="flat"
                                                    value={acct.routingNumber} onChange={(e) => updateBankAccount(idx, "routingNumber", e.target.value)} />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                <button type="button" className="btn btn-sm" style={{ backgroundColor: "#1B75F0", color: "#fff", borderRadius: 6 }} onClick={addBankAccount}>+ Add Another Bank Account</button>
                                {bankAccounts.length >= 2 && (
                                    <div className="mt-4">
                                        <p className={styles["info-title"]} style={{ fontSize: 14 }}>Direct Deposit Allocation</p>
                                        <p style={{ fontSize: 12, color: "#666", marginBottom: 12 }}>Allocate net pay across accounts by percentage or flat amount. One split may be &quot;Remaining Balance&quot;.</p>
                                        <table className="table table-sm" style={{ fontSize: 13 }}>
                                            <thead><tr><th>Account</th><th>Split Type</th><th>Value</th><th></th></tr></thead>
                                            <tbody>
                                                {addModeSplits.map((split, idx) => (
                                                    <tr key={idx}>
                                                        <td style={{ verticalAlign: "middle" }}>
                                                            <span style={{ fontSize: 13 }}>
                                                                {bankAccounts[idx]?.accountType ? String(bankAccounts[idx].accountType).charAt(0).toUpperCase() + String(bankAccounts[idx].accountType).slice(1) : "Account"} {bankAccounts[idx]?.accountNumber ? "****" + String(bankAccounts[idx].accountNumber).slice(-4) : "****"}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <select className="form-select form-select-sm" value={split.splitType || "remaining_balance"}
                                                                onChange={(e) => updateAddModeSplit(idx, "splitType", e.target.value)}>
                                                                <option value="remaining_balance">Remaining Balance</option>
                                                                <option value="percentage">Percentage (%)</option>
                                                                <option value="flat">Flat Amount ($)</option>
                                                            </select>
                                                        </td>
                                                        <td>
                                                            {split.splitType !== "remaining_balance" ? (
                                                                <input type="number" className="form-control form-control-sm"
                                                                    placeholder={split.splitType === "percentage" ? "e.g. 50" : "e.g. 500"}
                                                                    value={split.value ?? ""}
                                                                    onChange={(e) => updateAddModeSplit(idx, "value", e.target.value)} />
                                                            ) : (() => {
                                                                const usedPct = addModeSplits.filter((s, i) => i !== idx && s.splitType === "percentage").reduce((sum, s) => sum + (parseFloat(s.value) || 0), 0);
                                                                const rem = Math.max(0, 100 - usedPct);
                                                                return (
                                                                    <input type="text" className="form-control form-control-sm" readOnly
                                                                        value={Math.round(rem)}
                                                                        style={{ background: "#f0f9f0", color: "#155724", fontWeight: 600, cursor: "default", width: 60 }} />
                                                                );
                                                            })()}
                                                        </td>
                                                        <td>
                                                            {bankAccounts.length > 2 && (
                                                                <button type="button" className="btn btn-sm btn-outline-danger"
                                                                    onClick={() => removeBankAccount(idx)}>Remove</button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}
                        {paymentMethod === "check" && (
                            <p className="mt-2" style={{ fontSize: 13, color: "#888" }}>No bank account on file &mdash; employee will be paid by <strong>Check</strong>.</p>
                        )}
                    </div>
                )}
            </div>

            {/* Emergency Contact */}
            <div className={cn(styles["wage-info-container"], "mt-4")}>
                <p className={styles["info-title"]}>Emergency Contact</p>
                <div className="container-fluid">
                    <div className="row g-3">
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="First Name" variant="flat"
                                {...register(EmployeeInputs.EMERGENCY_FIRST_NAME)} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Last Name" variant="flat"
                                {...register(EmployeeInputs.EMERGENCY_LAST_NAME)} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Relationship" variant="flat"
                                {...register(EmployeeInputs.EMERGENCY_RELATIONSHIP)} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Primary Phone" variant="flat" placeholder="(555) 555-5555"
                                {...register(EmployeeInputs.EMERGENCY_PHONE)}
                                onChange={e => {
                                    const d = e.target.value.replace(/\D/g, '').slice(0, 10);
                                    const fmt = d.length <= 3 ? (d.length ? `(${d}` : '') : d.length <= 6 ? `(${d.slice(0,3)}) ${d.slice(3)}` : `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
                                    setValue(EmployeeInputs.EMERGENCY_PHONE, fmt, { shouldValidate: true });
                                }}
                                maxLength={14} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Alternate Phone" variant="flat" placeholder="(555) 555-5555 (optional)"
                                {...register(EmployeeInputs.EMERGENCY_ALT_PHONE)}
                                onChange={e => {
                                    const d = e.target.value.replace(/\D/g, '').slice(0, 10);
                                    const fmt = d.length <= 3 ? (d.length ? `(${d}` : '') : d.length <= 6 ? `(${d.slice(0,3)}) ${d.slice(3)}` : `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
                                    setValue(EmployeeInputs.EMERGENCY_ALT_PHONE, fmt, { shouldValidate: true });
                                }}
                                maxLength={14} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Email" variant="flat"
                                {...register(EmployeeInputs.EMERGENCY_EMAIL)} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Work Eligibility */}
            <div className={cn(styles["wage-info-container"], "mt-4")}>
                <p className={styles["info-title"]}>Work Eligibility & I-9</p>
                <div className="container-fluid">
                    <div className="row g-3">
                        <div className="col-lg-4 col-md-6 col-12">
                            <Select fullWidth label="Are you a U.S. Citizen?" placeholder="Select option"
                                options={[{ label: "Yes", value: "Yes" }, { label: "No", value: "No" }]}
                                {...register(EmployeeInputs.US_CITIZEN)} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Select fullWidth label="Authorized to Work in U.S.?" placeholder="Select option"
                                options={[{ label: "Yes", value: "Yes" }, { label: "No", value: "No" }]}
                                {...register(EmployeeInputs.AUTHORIZED_TO_WORK)} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Work Authorization Type" variant="flat" placeholder="Citizen, Permanent Resident, Visa, etc."
                                {...register(EmployeeInputs.WORK_AUTH_TYPE)} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Work Authorization Expiration" variant="flat" type="date"
                                {...register(EmployeeInputs.WORK_AUTH_EXPIRY)} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Select fullWidth label="Rehire Employee?" placeholder="Select option"
                                options={[{ label: "Yes", value: "Yes" }, { label: "No", value: "No" }]}
                                {...register(EmployeeInputs.REHIRE)} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Select fullWidth label="I-9 Section 1 Completed?" placeholder="Select option"
                                options={[{ label: "Yes", value: "Yes" }, { label: "No", value: "No" }]}
                                {...register(EmployeeInputs.I9_SECTION1)} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="I-9 Completion Date" variant="flat" type="date"
                                {...register(EmployeeInputs.I9_DATE)} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Select fullWidth label="Primary Work State" placeholder="Select state" options={US_STATES}
                                {...register(EmployeeInputs.PRIMARY_WORK_STATE)} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Select fullWidth label="Remote Work State (if different)" placeholder="Select state" options={US_STATES}
                                {...register(EmployeeInputs.REMOTE_WORK_STATE)} />
                        </div>
                        <div className="col-lg-4 col-md-6 col-12">
                            <Input fullWidth label="Workers Comp Class Code" variant="flat" placeholder="e.g. 8810"
                                {...register(EmployeeInputs.WORKERS_COMP_CODE)} />
                        </div>
                    </div>
                </div>
            </div>

            {(isError || submitError) && (
                <p className="text-danger mt-2">
                    {submitError || error?.response?.data?.message || "Failed to save employee. Please try again."}
                </p>
            )}

            {!isEditMode && !fromOnboarding && (
                <div style={{ margin: '16px 0', padding: '14px 18px', background: '#f0fdf4', border: '1.5px solid #bbf7d0', borderRadius: 10, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <input
                        type="checkbox"
                        id="sendInviteEmp"
                        checked={sendInvite}
                        onChange={e => setSendInvite(e.target.checked)}
                        style={{ marginTop: 3, accentColor: '#16a34a', width: 16, height: 16, cursor: 'pointer' }}
                    />
                    <label htmlFor="sendInviteEmp" style={{ cursor: 'pointer', margin: 0 }}>
                        <span style={{ fontWeight: 600, color: '#15803d', fontSize: 14 }}>Send Portal Invitation Email</span>
                        <span style={{ display: 'block', fontSize: 12, color: '#166534', marginTop: 2 }}>
                            Employee will receive a welcome email with login credentials for payrollnoot.com
                        </span>
                    </label>
                </div>
            )}

            {isEditMode && !fromOnboarding && (
                <div style={{ margin: '16px 0', padding: '14px 18px', background: '#f0fdf4', border: '1.5px solid #bbf7d0', borderRadius: 10 }}>
                    <p style={{ fontWeight: 600, color: '#15803d', fontSize: 14, margin: '0 0 4px 0' }}>Portal Access</p>
                    <p style={{ fontSize: 12, color: '#166534', margin: '0 0 10px 0' }}>
                        Send a "Set My Password" email so this employee can access their portal. Has no effect if they already have an account.
                    </p>
                    {inviteResult === 'sent' && <p style={{ color: '#15803d', fontSize: 13, margin: '0 0 8px 0' }}>✓ Invite sent successfully.</p>}
                    {inviteResult === 'exists' && <p style={{ color: '#92400e', fontSize: 13, margin: '0 0 8px 0' }}>Portal account already exists for this employee.</p>}
                    {inviteResult === 'error' && <p style={{ color: '#dc2626', fontSize: 13, margin: '0 0 8px 0' }}>Failed to send invite. Please try again.</p>}
                    <button
                        type="button"
                        style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                        onClick={async () => {
                            setInviteResult(null);
                            try {
                                const inv = await inviteEmployee(employeeId);
                                setInviteResult(inv?.alreadyExists ? 'exists' : 'sent');
                            } catch {
                                setInviteResult('error');
                            }
                        }}
                    >
                        Send Portal Invite
                    </button>
                </div>
            )}

            <div className={styles["bottom-container"]}>
                <Button buttonType="flat" type="submit" disabled={isPending || isSubmitting}>
                    {(isPending || isSubmitting) ? "Saving..." : isEditMode ? "Update Employee" : "Add Employee"}
                </Button>
                <Button variant="text" type="button" onClick={() => navigate("/employees")}>Cancel</Button>
            </div>
        </form>
    );
};

export default AddEditEmployee;




































